"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as analytics from "./lib/analytics";
import { excludeSelectedStations } from "./lib/stations";
import { dedupeBuses } from "./lib/timetable";
import * as Sentry from "@sentry/nextjs";
import SiteFooter from "./components/SiteFooter";
import RouteLinks from "./components/RouteLinks";
import { humanize, popularRouteLinks, relatedRoutes } from "./lib/routes";

export interface Station {
  stationID: number;
  stationName: string;
}

export interface BusTimetable {
  dutyID: number;
  routationID: number;
  tripID: number;
  boardingStationID: number;
  destinationStationID: number;
  srNo: number;
  tripTime: string;
  direction: string;
  routationSrNo: number;
  boardingStationName: string;
  destinationStationName: string;
  tripName: string;
  vehicleNumber: string;
  vehicleType1: string;
  depotID: number;
  depotName: string;
  dt: string;
}

interface RecentSearch {
  from: Station[];
  to: Station[];
  timestamp: number;
}

interface FavouriteRoute {
  dutyID: number;
  from: Station[];
  to: Station[];
  label: string;
  tripName: string;
  tripTime: string;
}

export interface RouteMeta {
  busCount: number;
  verified: boolean;
  lastSeen: string;
  isIndexable: boolean;
}

// Skeleton loading cards (4 shimmer placeholders)
function SkeletonCards() {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading timetable">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="p-4 bg-white dark:bg-slate-900 border border-slate-200/50 dark:border-slate-900 rounded-xl flex justify-between items-center" style={{ animationDelay: `${i * 80}ms` }}>
          <div className="space-y-2 flex-1">
            <div className="skeleton h-4 w-20" />
            <div className="skeleton h-3 w-40" />
          </div>
          <div className="space-y-2 items-end flex flex-col">
            <div className="skeleton h-4 w-14" />
            <div className="skeleton h-3 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Sleeping bus — shown when no results found
function SleepingBus() {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <div className="relative">
        {/* Bus body */}
        <svg viewBox="0 0 64 36" className="w-24 h-14 text-slate-300 dark:text-slate-700" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="56" height="24" rx="4" fill="currentColor" />
          <rect x="8" y="8" width="10" height="8" rx="1.5" fill="white" opacity="0.6" />
          <rect x="22" y="8" width="10" height="8" rx="1.5" fill="white" opacity="0.6" />
          <rect x="36" y="8" width="10" height="8" rx="1.5" fill="white" opacity="0.6" />
          <rect x="4" y="18" width="56" height="3" fill="#f97316" opacity="0.5" />
          <circle cx="16" cy="30" r="5" fill="#475569" />
          <circle cx="16" cy="30" r="2" fill="#94a3b8" />
          <circle cx="48" cy="30" r="5" fill="#475569" />
          <circle cx="48" cy="30" r="2" fill="#94a3b8" />
        </svg>
        {/* zzz bubbles */}
        <span className="zzz-1 absolute -top-1 -right-1 text-xs font-black text-slate-400 dark:text-slate-500 select-none">z</span>
        <span className="zzz-2 absolute -top-4 right-2  text-sm font-black text-slate-400 dark:text-slate-500 select-none">z</span>
        <span className="zzz-3 absolute -top-7 right-4  text-base font-black text-slate-400 dark:text-slate-500 select-none">Z</span>
      </div>
      <p className="text-xs font-semibold text-slate-400 dark:text-slate-500">No buses found between these stations.</p>
    </div>
  );
}

// Entertaining driving bus SVG animation component for wait states
function LoadingBus() {
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-3" aria-live="polite">
      <div className="relative w-32 h-14 flex items-center justify-center overflow-hidden">
        {/* Animated Bus Wrapper */}
        <div className="animate-bus-drive flex flex-col items-center">
          <div className="animate-bus-bounce relative w-16 h-8 bg-blue-600 dark:bg-blue-500 rounded-md flex flex-col justify-between p-1.5 shadow-sm">
            {/* Windows */}
            <div className="flex gap-0.5 justify-around w-full">
              <div className="w-3.5 h-2 bg-slate-100 dark:bg-slate-900 rounded-xs"></div>
              <div className="w-3.5 h-2 bg-slate-100 dark:bg-slate-900 rounded-xs"></div>
              <div className="w-3.5 h-2 bg-slate-100 dark:bg-slate-900 rounded-xs"></div>
            </div>
            {/* Orange/Saffron Brand Stripe */}
            <div className="w-full h-1 bg-orange-500 my-0.5"></div>
            {/* Wheels */}
            <div className="absolute -bottom-1.5 left-2.5 w-3.5 h-3.5 bg-slate-800 rounded-full border border-slate-300 dark:border-slate-700 flex items-center justify-center">
              <div className="animate-wheel w-2 h-2 rounded-full border border-dashed border-white"></div>
            </div>
            <div className="absolute -bottom-1.5 right-2.5 w-3.5 h-3.5 bg-slate-800 rounded-full border border-slate-300 dark:border-slate-700 flex items-center justify-center">
              <div className="animate-wheel w-2 h-2 rounded-full border border-dashed border-white"></div>
            </div>
          </div>
        </div>
      </div>
      {/* Driving track line */}
      <div className="w-24 h-0.5 bg-slate-200 dark:bg-slate-800 -mt-1 rounded"></div>
      <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold animate-pulse uppercase tracking-wider">
        Driving...
      </p>
    </div>
  );
}

export default function Home({
  slug,
  routeMeta,
  initialFrom = [],
  initialTo = [],
  initialTimetable = [],
}: {
  slug?: string;
  routeMeta?: RouteMeta;
  initialFrom?: Station[];
  initialTo?: Station[];
  initialTimetable?: BusTimetable[];
}) {
  // Derive the current city pair from the slug (e.g. "delhi-to-chandigarh")
  // to drive SEO internal links. Falls back to the homepage popular grid.
  const slugParts = slug?.split("-to-") ?? [];
  const currentRoute =
    slugParts.length === 2
      ? { from: humanize(slugParts[0]), to: humanize(slugParts[1]) }
      : null;
  // PWA Service Worker Registration
  useEffect(() => {
    if (typeof window !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch((err) => {
        Sentry.captureException(err);
        console.error("Service worker registration failed:", err);
      });
    }
  }, []);

  // PostHog Analytics — lazy-loaded after the page is interactive.
  useEffect(() => {
    analytics.initAnalytics();
  }, []);

  // Theme State & Sync
  const [theme, setTheme] = useState<"light" | "dark">("light");

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle("dark", savedTheme === "dark");
    } else {
      // Feature 12: Dark mode auto-schedule (8 PM – 6 AM) when no manual pref set
      const hour = new Date().getHours();
      const autoDark = hour >= 20 || hour < 6;
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches || autoDark;
      setTheme(prefersDark ? "dark" : "light");
      document.documentElement.classList.toggle("dark", prefersDark);
    }
  }, []);

  // Feature 12: Re-evaluate auto dark every hour when no manual pref
  useEffect(() => {
    const interval = setInterval(() => {
      const saved = localStorage.getItem("theme");
      if (!saved) {
        const hour = new Date().getHours();
        const autoDark = hour >= 20 || hour < 6;
        document.documentElement.classList.toggle("dark", autoDark);
        setTheme(autoDark ? "dark" : "light");
      }
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
    analytics.capture("theme_toggled", { theme: newTheme });
  };

  // Station search states
  const [fromStations, setFromStations] = useState<Station[]>(initialFrom);
  const [toStations, setToStations] = useState<Station[]>(initialTo);

  const [fromQuery, setFromQuery] = useState("");
  const [toQuery, setToQuery] = useState("");

  const [fromSuggestions, setFromSuggestions] = useState<Station[]>([]);
  const [toSuggestions, setToSuggestions] = useState<Station[]>([]);

  const [isLoadingFrom, setIsLoadingFrom] = useState(false);
  const [isLoadingTo, setIsLoadingTo] = useState(false);

  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);

  // Timetable and stops states
  const [timetable, setTimetable] = useState<BusTimetable[]>(initialTimetable);
  const [isLoadingTimetable, setIsLoadingTimetable] = useState(false);
  const [hasSearched, setHasSearched] = useState(initialTimetable.length > 0);
  const [searchError, setSearchError] = useState("");
  // Form-level error for free-typed station text that couldn't be resolved.
  const [formError, setFormError] = useState("");
  const [isResolving, setIsResolving] = useState(false);

  const [selectedBus, setSelectedBus] = useState<BusTimetable | null>(null);
  const [busStops, setBusStops] = useState<BusTimetable[]>([]);
  const [isLoadingStops, setIsLoadingStops] = useState(false);
  const [isStopsOpen, setIsStopsOpen] = useState(false);

  // Recent Searches state
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  // Feature 2: Favourite Routes
  const [favourites, setFavourites] = useState<FavouriteRoute[]>([]);

  // Feature 4: Bus type filter
  const [activeFilter, setActiveFilter] = useState<string>("All");

  // Feature 1: Next bus countdown.
  // `mounted` gates all time-relative UI so it renders only on the client —
  // server render time and client hydration time differ (elapsed time +
  // timezone), which would cause a hydration text mismatch (React #418).
  const [now, setNow] = useState<Date>(new Date());
  const [mounted, setMounted] = useState(false);

  // Feature 5: PWA install prompt
  const pwaPromptRef = useRef<any>(null);
  const [showPwaPrompt, setShowPwaPrompt] = useState(false);

  // Share button state
  const [shareCopied, setShareCopied] = useState(false);
  const [totalSearches, setTotalSearches] = useState(0);

  // Keyboard shortcut hint state
  const [showKbHint, setShowKbHint] = useState(true);
  const fromInputRef = useRef<HTMLInputElement>(null);

  // Focus recovery references
  const triggerElementRef = useRef<HTMLDivElement | null>(null);
  const modalCloseBtnRef = useRef<HTMLButtonElement | null>(null);

  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);

  // Tiny haptic helper — degrades gracefully
  const haptic = useCallback((ms: number) => { navigator.vibrate?.(ms); }, []);

  // Fetch recent searches + favourites + stats on mount
  useEffect(() => {
    const history = localStorage.getItem("recent_searches");
    if (history) {
      try { setRecentSearches(JSON.parse(history)); } catch (e) {}
    }
    const favs = localStorage.getItem("favourite_routes");
    if (favs) {
      try { setFavourites(JSON.parse(favs)); } catch (e) {}
    }
    // Fetch motivator stats
    const fetchStats = async () => {
      try {
        const res = await fetch("/api/stats");
        const data = await res.json();
        if (data.totalSearches) setTotalSearches(data.totalSearches);
      } catch (err) {}
    };
    fetchStats();

    // Fade keyboard hint after 4s
    const t = setTimeout(() => setShowKbHint(false), 4000);
    return () => clearTimeout(t);
  }, []);

  // Feature 1: Tick 'now' every 30 seconds for live countdown
  useEffect(() => {
    setMounted(true);
    setNow(new Date());
    const interval = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(interval);
  }, []);

  // Feature 5: PWA install prompt
  useEffect(() => {
    const dismissed = localStorage.getItem("pwa_dismissed");
    if (dismissed) return;
    const handler = (e: Event) => {
      e.preventDefault();
      pwaPromptRef.current = e;
      setShowPwaPrompt(true);
    };
    window.addEventListener("beforeinstallprompt", handler as EventListener);
    return () => window.removeEventListener("beforeinstallprompt", handler as EventListener);
  }, []);

  // Feature 8: '/' keyboard shortcut to focus From input
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        fromInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Read URL params on mount and auto-trigger search if present
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const fromParam = params.get("from");
    const toParam = params.get("to");
    if (!fromParam || !toParam) return;

    // Parse comma-separated "ID:Name" pairs e.g. "123:Bawal,456:Rewari"
    const parseStations = (raw: string): Station[] =>
      raw.split(",").map((part) => {
        const [id, ...nameParts] = part.split(":");
        return { stationID: Number(id), stationName: nameParts.join(":") };
      }).filter((s) => !isNaN(s.stationID));

    const fromList = parseStations(fromParam);
    const toList = parseStations(toParam);
    if (fromList.length === 0 || toList.length === 0) return;

    setFromStations(fromList);
    setToStations(toList);
    executeTimetableSearch(fromList, toList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve dynamic URL slug and auto-trigger search if present (skip if SSR loaded it)
  useEffect(() => {
    if (!slug || initialTimetable.length > 0) return;
    const parts = slug.split("-to-");
    if (parts.length === 2) {
      const fromName = parts[0].replace(/-/g, " ");
      const toName = parts[1].replace(/-/g, " ");

      const resolveAndSearch = async () => {
        setIsLoadingTimetable(true);
        setHasSearched(true);
        setSearchError("");

        const fromStation = await resolveStation(fromName);
        const toStation = await resolveStation(toName);

        if (fromStation && toStation) {
          setFromStations([fromStation]);
          setToStations([toStation]);
          executeTimetableSearch([fromStation], [toStation]);
        } else {
          setSearchError(`Could not find stations matching "${fromName}" and "${toName}".`);
          setIsLoadingTimetable(false);
        }
      };

      resolveAndSearch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Debounced station suggestion fetching
  useEffect(() => {
    if (!fromQuery.trim()) {
      setFromSuggestions([]);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setIsLoadingFrom(true);
      try {
        const res = await fetch(`/api/stations?q=${encodeURIComponent(fromQuery)}`);
        const data = await res.json();
        if (data.model) setFromSuggestions(data.model);
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
      } finally {
        setIsLoadingFrom(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [fromQuery]);

  useEffect(() => {
    if (!toQuery.trim()) {
      setToSuggestions([]);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setIsLoadingTo(true);
      try {
        const res = await fetch(`/api/stations?q=${encodeURIComponent(toQuery)}`);
        const data = await res.json();
        if (data.model) setToSuggestions(data.model);
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
      } finally {
        setIsLoadingTo(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [toQuery]);

  // Click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (fromRef.current && !fromRef.current.contains(event.target as Node)) {
        setShowFromDropdown(false);
      }
      if (toRef.current && !toRef.current.contains(event.target as Node)) {
        setShowToDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Escape key to close stops modal
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeStopsModal();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedBus]);

  // Manage focus when modal opens/closes
  useEffect(() => {
    if (isStopsOpen && modalCloseBtnRef.current) {
      modalCloseBtnRef.current.focus();
    }
  }, [isStopsOpen]);

  const addFromStation = (station: Station) => {
    if (!fromStations.some((s) => s.stationID === station.stationID)) {
      setFromStations([...fromStations, station]);
      analytics.capture("boarding_station_added", { name: station.stationName });
    }
    setFromQuery("");
    setShowFromDropdown(false);
    setFormError("");
  };

  const removeFromStation = (stationID: number) => {
    const station = fromStations.find((s) => s.stationID === stationID);
    setFromStations(fromStations.filter((s) => s.stationID !== stationID));
    if (station) {
      analytics.capture("boarding_station_removed", { name: station.stationName });
    }
  };

  const addToStation = (station: Station) => {
    if (!toStations.some((s) => s.stationID === station.stationID)) {
      setToStations([...toStations, station]);
      analytics.capture("destination_station_added", { name: station.stationName });
    }
    setToQuery("");
    setShowToDropdown(false);
    setFormError("");
  };

  const removeToStation = (stationID: number) => {
    const station = toStations.find((s) => s.stationID === stationID);
    setToStations(toStations.filter((s) => s.stationID !== stationID));
    if (station) {
      analytics.capture("destination_station_removed", { name: station.stationName });
    }
  };

  const clearFromStations = () => {
    setFromStations([]);
    setFromQuery("");
    setShowFromDropdown(false);
    analytics.capture("boarding_stations_cleared");
  };

  const clearToStations = () => {
    setToStations([]);
    setToQuery("");
    setShowToDropdown(false);
    analytics.capture("destination_stations_cleared");
  };

  const resetSearch = () => {
    // Clear inputs
    clearFromStations();
    clearToStations();

    // Clear results so the UI returns to its initial state instead of
    // showing stale buses for a route that is no longer selected.
    setHasSearched(false);
    setTimetable([]);
    setSearchError("");
    setFormError("");
    setActiveFilter("All");
    setSelectedBus(null);

    // Restore the URL to root so the shareable/SEO route is cleared too.
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/");
    }

    analytics.capture("search_reset");
  };

  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");
  };

  // Normalize a station name for tolerant comparison: lowercase, drop
  // punctuation, collapse whitespace.
  const normalizeName = (s: string): string =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

  // Fetch raw station candidates for free-typed text, with a typo-tolerant
  // retry. Mobile autocorrect often appends/garbles trailing words, so if the
  // full query returns nothing we retry with just the first word.
  const fetchStationCandidates = async (query: string): Promise<Station[]> => {
    const q = query.trim();
    if (!q) return [];
    const hit = async (term: string): Promise<Station[]> => {
      try {
        const res = await fetch(`/api/stations?q=${encodeURIComponent(term)}`);
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data.model) ? data.model : [];
      } catch (e) {
        Sentry.captureException(e);
        return [];
      }
    };
    let results = await hit(q);
    if (results.length === 0) {
      const firstWord = q.split(/\s+/)[0];
      if (firstWord && firstWord !== q) results = await hit(firstWord);
    }
    return results;
  };

  // Pick the best candidate for a free-typed query (exact → prefix → contains → first).
  const pickBestStation = (candidates: Station[], query: string): Station | null => {
    if (candidates.length === 0) return null;
    const q = normalizeName(query);
    return (
      candidates.find((s) => normalizeName(s.stationName) === q) ||
      candidates.find((s) => normalizeName(s.stationName).startsWith(q)) ||
      candidates.find((s) => normalizeName(s.stationName).includes(q)) ||
      candidates[0]
    );
  };

  const resolveStation = async (query: string): Promise<Station | null> => {
    const candidates = await fetchStationCandidates(query);
    return pickBestStation(candidates, query);
  };

  // Build a shareable URL for the current search
  const buildShareUrl = (boarding: Station[], destination: Station[]): string => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    if (boarding.length === 1 && destination.length === 1) {
      return `${base}/${slugify(boarding[0].stationName)}-to-${slugify(destination[0].stationName)}`;
    }
    const fromParam = boarding.map((s) => `${s.stationID}:${s.stationName}`).join(",");
    const toParam = destination.map((s) => `${s.stationID}:${s.stationName}`).join(",");
    return `${base}/?from=${encodeURIComponent(fromParam)}&to=${encodeURIComponent(toParam)}`;
  };

  const handleShare = async () => {
    if (fromStations.length === 0 || toStations.length === 0) return;
    const url = buildShareUrl(fromStations, toStations);
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Haryana Roadways — ${displayNames(fromStations)} → ${displayNames(toStations)}`,
          text: `Bus timetable from ${displayNames(fromStations)} to ${displayNames(toStations)}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      }
      analytics.capture("timetable_shared", {
        from: fromStations.map((s) => s.stationName).join(","),
        to: toStations.map((s) => s.stationName).join(","),
      });
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  // Shared implementation of searching timetable api calls
  const executeTimetableSearch = async (boarding: Station[], destination: Station[]) => {
    setIsLoadingTimetable(true);
    setHasSearched(true);
    setSearchError("");
    setTimetable([]);
    setActiveFilter("All"); // Feature 4: reset filter on new search
    haptic(15);

    analytics.capture("search_timetable_clicked", {
      boardingCount: boarding.length,
      destinationCount: destination.length,
    });

    // Push route to URL so it is bookmarkable & shareable
    if (typeof window !== "undefined") {
      const url = buildShareUrl(boarding, destination);
      window.history.pushState({}, "", url);
    }

    try {
      const res = await fetch("/api/timetable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          boardingStationList: boarding,
          destinationStationList: destination,
        }),
      });

      if (!res.ok) throw new Error("Failed to load schedules.");
      const data = await res.json();
      if (data.model) {
        const sortedModel = [...data.model].sort((a: BusTimetable, b: BusTimetable) => {
          const timeA = new Date(a.tripTime);
          const minsA = timeA.getHours() * 60 + timeA.getMinutes();
          const timeB = new Date(b.tripTime);
          const minsB = timeB.getHours() * 60 + timeB.getMinutes();
          return minsA - minsB;
        });

        // One upstream row per (trip, matching station) pair means the same
        // departure repeats once per selected station — collapse it here so the
        // list, the filter counts and the analytics all report the same number.
        const uniqueModel = dedupeBuses(sortedModel);
        setTimetable(uniqueModel);
        saveSearchToHistory(boarding, destination);
        incrementRouteCount(boarding, destination);
        setTotalSearches((prev) => (prev > 0 ? prev + 1 : prev));

        if (uniqueModel.length > 0) {
          analytics.capture("timetable_results_loaded", {
            from: boarding.map((s) => s.stationName).join(","),
            to: destination.map((s) => s.stationName).join(","),
            count: uniqueModel.length,
          });
        } else {
          analytics.capture("no_buses_found", {
            from: boarding.map((s) => s.stationName).join(","),
            to: destination.map((s) => s.stationName).join(","),
          });
        }

        // Feature 11: Confetti on very first successful search
        const alreadySearched = localStorage.getItem("has_searched");
        if (!alreadySearched && uniqueModel.length > 0) {
          localStorage.setItem("has_searched", "true");
          void import("canvas-confetti").then(({ default: confetti }) =>
            confetti({ particleCount: 80, spread: 70, origin: { y: 0.5 }, zIndex: 9999 })
          );
        }
      } else {
        analytics.capture("no_buses_found", {
          from: boarding.map((s) => s.stationName).join(","),
          to: destination.map((s) => s.stationName).join(","),
        });
        setTimetable([]);
      }
    } catch (err: any) {
      Sentry.captureException(err);
      setSearchError(err.message || "Something went wrong.");
    } finally {
      setIsLoadingTimetable(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    let from = fromStations;
    let to = toStations;

    // Mobile users often type a station and hit Search without tapping a
    // dropdown suggestion (autocorrect, fat-finger, habit). Resolve any
    // free-typed text to the closest matching station before searching.
    if (from.length === 0 || to.length === 0) {
      setIsResolving(true);
      try {
        if (from.length === 0 && fromQuery.trim()) {
          const match = await resolveStation(fromQuery);
          if (match) {
            from = [match];
            setFromStations(from);
            setFromQuery("");
            setShowFromDropdown(false);
            analytics.capture("station_resolved_from_text", { field: "boarding", name: match.stationName });
          }
        }
        if (to.length === 0 && toQuery.trim()) {
          const match = await resolveStation(toQuery);
          if (match) {
            to = [match];
            setToStations(to);
            setToQuery("");
            setShowToDropdown(false);
            analytics.capture("station_resolved_from_text", { field: "destination", name: match.stationName });
          }
        }
      } finally {
        setIsResolving(false);
      }
    }

    // Still missing a station — guide the user instead of a dead button.
    const missing: string[] = [];
    if (from.length === 0) {
      missing.push(fromQuery.trim() ? `a boarding station matching “${fromQuery.trim()}”` : "a boarding station");
      if (fromQuery.trim()) setShowFromDropdown(true);
    }
    if (to.length === 0) {
      missing.push(toQuery.trim() ? `a destination station matching “${toQuery.trim()}”` : "a destination station");
      if (toQuery.trim()) setShowToDropdown(true);
    }
    if (missing.length > 0) {
      setFormError(`We couldn't find ${missing.join(" and ")}. Please pick from the suggestions.`);
      return;
    }

    executeTimetableSearch(from, to);
  };

  const saveSearchToHistory = (from: Station[], to: Station[]) => {
    let list: RecentSearch[] = [];
    const history = localStorage.getItem("recent_searches");
    if (history) { try { list = JSON.parse(history); } catch (e) {} }

    const isSame = (a: Station[], b: Station[]) => {
      if (a.length !== b.length) return false;
      return a.map((s) => s.stationID).sort().join(",") === b.map((s) => s.stationID).sort().join(",");
    };

    list = list.filter((item) => !(isSame(item.from, from) && isSame(item.to, to)));
    list.unshift({ from, to, timestamp: Date.now() });
    list = list.slice(0, 5);
    setRecentSearches(list);
    localStorage.setItem("recent_searches", JSON.stringify(list));
  };

  // Feature 10: Track route popularity
  const incrementRouteCount = (from: Station[], to: Station[]) => {
    if (typeof window === "undefined") return;
    const key = `${from.map((s) => s.stationID).join("+")}:${to.map((s) => s.stationID).join("+")}`;
    const raw = localStorage.getItem("route_counts");
    const counts: Record<string, number> = raw ? JSON.parse(raw) : {};
    counts[key] = (counts[key] || 0) + 1;
    localStorage.setItem("route_counts", JSON.stringify(counts));
  };

  const getRouteCount = (from: Station[], to: Station[]): number => {
    if (typeof window === "undefined") return 0;
    const key = `${from.map((s) => s.stationID).join("+")}:${to.map((s) => s.stationID).join("+")}`;
    const raw = localStorage.getItem("route_counts");
    const counts: Record<string, number> = raw ? JSON.parse(raw) : {};
    return counts[key] || 0;
  };

  // Feature 2: Toggle a favourite for a specific bus trip (per dutyID)
  const toggleFavourite = (bus: BusTimetable) => {
    haptic(20);
    const label = `${displayNames(fromStations)} → ${displayNames(toStations)}`;
    const exists = favourites.some((f) => f.dutyID === bus.dutyID);
    let next: FavouriteRoute[];
    if (exists) {
      next = favourites.filter((f) => f.dutyID !== bus.dutyID);
    } else {
      next = [{ dutyID: bus.dutyID, from: fromStations, to: toStations, label, tripName: bus.tripName || label, tripTime: bus.tripTime }, ...favourites].slice(0, 20);
    }
    setFavourites(next);
    localStorage.setItem("favourite_routes", JSON.stringify(next));
    analytics.capture(exists ? "favourite_removed" : "favourite_added", { label, dutyID: bus.dutyID });
  };

  const isFavourite = (bus: BusTimetable): boolean => {
    return favourites.some((f) => f.dutyID === bus.dutyID);
  };

  const handleRecentSearchClick = (search: { from: Station[]; to: Station[] }) => {
    setFromStations(search.from);
    setToStations(search.to);
    setFromQuery("");
    setToQuery("");
    analytics.capture("recent_search_clicked", {
      from: search.from.map((s) => s.stationName).join(","),
      to: search.to.map((s) => s.stationName).join(","),
    });
    executeTimetableSearch(search.from, search.to);
  };

  const deleteRecentSearch = (index: number) => {
    const search = recentSearches[index];
    const list = recentSearches.filter((_, i) => i !== index);
    setRecentSearches(list);
    localStorage.setItem("recent_searches", JSON.stringify(list));
    if (search) {
      analytics.capture("recent_search_deleted", {
        from: search.from.map((s) => s.stationName).join(","),
        to: search.to.map((s) => s.stationName).join(","),
      });
    }
  };

  const swapStations = () => {
    haptic(10);
    const tempFrom = [...fromStations];
    setFromStations([...toStations]);
    setToStations(tempFrom);

    const tempQuery = fromQuery;
    setFromQuery(toQuery);
    setToQuery(tempQuery);

    analytics.capture("stations_swapped", {
      fromCount: toStations.length,
      toCount: fromStations.length,
    });
  };

  const handlePwaInstall = async () => {
    haptic(15);
    const promptEvent = pwaPromptRef.current;
    if (!promptEvent) return;
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    analytics.capture("pwa_install_choice", { outcome });
    setShowPwaPrompt(false);
    pwaPromptRef.current = null;
  };

  const handlePwaDismiss = () => {
    haptic(5);
    localStorage.setItem("pwa_dismissed", "true");
    setShowPwaPrompt(false);
    analytics.capture("pwa_install_dismissed");
  };

  const handleBusClick = async (bus: BusTimetable, elementId: string) => {
    haptic(10);
    triggerElementRef.current = document.getElementById(elementId) as HTMLDivElement;
    setSelectedBus(bus);
    setIsLoadingStops(true);
    setIsStopsOpen(true);
    setBusStops([]);

    analytics.capture("bus_card_clicked", {
      dutyID: bus.dutyID,
      tripName: bus.tripName,
      depot: bus.depotName,
    });

    try {
      const res = await fetch(`/api/stops?type=route&dutyId=${bus.dutyID}`);
      if (!res.ok) throw new Error("Failed to load stops.");
      const data = await res.json();
      if (data.model) setBusStops(data.model);
    } catch (err: any) {
      Sentry.captureException(err);
      console.error(err);
    } finally {
      setIsLoadingStops(false);
    }
  };

  const closeStopsModal = () => {
    setIsStopsOpen(false);
    setSelectedBus(null);
    if (triggerElementRef.current) {
      triggerElementRef.current.focus();
    }
  };

  // Format route stops into a shareable text message
  const shareRouteStops = async () => {
    if (!selectedBus || busStops.length === 0) return;

    const header = `🚌 Haryana Roadways — ${selectedBus.tripName || selectedBus.boardingStationName + " → " + selectedBus.destinationStationName}`;
    const meta = `🪪 Bus: ${selectedBus.vehicleNumber?.trim() || "HR-XX-XXXX"} | ${selectedBus.vehicleType1 || "Ordinary"} | ${selectedBus.depotName} Depot`;
    const stops = busStops
      .map((stop, i) => {
        const time = formatTime(stop.tripTime);
        const prefix = i === 0 ? "🟠" : i === busStops.length - 1 ? "🟢" : "🔵";
        return `${prefix} ${stop.boardingStationName} — ${time}`;
      })
      .join("\n");
    const disclaimer = `\n⚠️ Timings may vary by a few minutes. Verify with the depot.`;
    const shareUrl = buildShareUrl(fromStations, toStations);
    const text = `${header}\n${meta}\n\n${stops}${disclaimer}\n\n🔗 ${shareUrl}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: header,
          text,
        });
      } else {
        await navigator.clipboard.writeText(text);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      }
      analytics.capture("route_stops_shared", {
        tripName: selectedBus.tripName,
        stopCount: busStops.length,
      });
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const formatTime = (timeStr: string) => {
    try {
      const date = new Date(timeStr);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return timeStr;
    }
  };

  const displayNames = (stations: Station[]) => {
    return stations
      .map((s) => s.stationName.charAt(0) + s.stationName.slice(1).toLowerCase())
      .join(", ");
  };

  // Keep the document title route-specific for multi-station searches, which load
  // via ?from=&to= query params and never reach the SSR [slug] route's generateMetadata.
  useEffect(() => {
    if (fromStations.length > 0 && toStations.length > 0) {
      document.title = `${displayNames(fromStations)} to ${displayNames(toStations)} Bus Timetable, Timings & Schedule | Haryana Roadways`;
    } else {
      document.title = "Haryana Roadways Bus Timetable — Schedules & Route Search";
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromStations, toStations]);

  // Already-added stations are dropped from their own dropdown: leaving them in
  // as ticked rows offers the user a click that does nothing. Filtering the
  // rendered list (not the fetched state) means removing a chip reveals the
  // station again without another request.
  const availableFromSuggestions = useMemo(
    () => excludeSelectedStations(fromSuggestions, fromStations),
    [fromSuggestions, fromStations]
  );
  const availableToSuggestions = useMemo(
    () => excludeSelectedStations(toSuggestions, toStations),
    [toSuggestions, toStations]
  );

  // Feature 4: derived filtered timetable and vehicle types
  const filteredTimetable = activeFilter === "All"
    ? timetable
    : timetable.filter((b) => b.vehicleType1 === activeFilter);

  const vehicleTypes: string[] = Array.from(new Set(timetable.map((b) => b.vehicleType1).filter(Boolean)));

  // Feature 1: next upcoming bus and countdown label.
  // Gated on `mounted` so this time-relative UI is client-only (avoids the
  // SSR/hydration text mismatch — see the `mounted` state declaration).
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const nextBus = mounted
    ? filteredTimetable.find((b) => {
        const d = new Date(b.tripTime);
        return d.getHours() * 60 + d.getMinutes() > nowMins;
      })
    : undefined;
  const allPast = mounted && filteredTimetable.length > 0 && !nextBus;

  const countdownLabel = (() => {
    if (!nextBus) return null;
    const d = new Date(nextBus.tripTime);
    const diffMins = d.getHours() * 60 + d.getMinutes() - nowMins;
    if (diffMins <= 0) return null;
    if (diffMins < 60) return `${diffMins} min`;
    const h = Math.floor(diffMins / 60);
    const m = diffMins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  })();

  // Compile route facts from the returned timetable. These paragraphs are
  // intentionally deterministic and data-led; route pages should explain the
  // result users are viewing, not rotate copy to create superficial variants.
  let totalBuses = 0;
  let firstBusTime = "";
  let lastBusTime = "";
  let depotsList = "";
  let busTypesList = "";
  let travelGuideIntro = "";
  let travelGuideStats = "";
  let travelGuideTypes = "";
  let travelGuideTips = "";

  if (currentRoute && timetable.length > 0) {
    totalBuses = timetable.length;
    const sorted = [...timetable].sort((a, b) => a.tripTime.localeCompare(b.tripTime));
    firstBusTime = sorted[0]?.tripTime || "";
    lastBusTime = sorted[sorted.length - 1]?.tripTime || "";
    const uniqueDepots = Array.from(new Set(timetable.map((b) => b.depotName).filter(Boolean)));
    depotsList = uniqueDepots.length > 0 ? uniqueDepots.slice(0, 3).join(", ") : "the listed operating depots";
    const uniqueTypes = Array.from(new Set(timetable.map((b) => b.vehicleType1 || "Ordinary").filter(Boolean)));
    busTypesList = uniqueTypes.length > 0 ? uniqueTypes.join(", ") : "Ordinary";

    travelGuideIntro = `This page brings together the available Haryana Roadways timetable information for travel from ${currentRoute.from} to ${currentRoute.to}. Use the departure times and service details above to compare options for your journey. The results are a planning aid rather than a guarantee of live vehicle movement, so confirm important trips with the relevant bus stand or depot.`;
    travelGuideStats = `The timetable currently contains ${totalBuses} scheduled service${totalBuses === 1 ? "" : "s"}. The earliest listed departure is ${formatTime(firstBusTime)}, and the latest listed departure is ${formatTime(lastBusTime)}. The result includes services associated with ${depotsList}. Timings and operating patterns can change because of seasonal schedules, route changes, traffic, or operational decisions.`;
    travelGuideTypes = `The service categories shown in this result are ${busTypesList}. Read the vehicle type and trip name on each result before choosing a bus, because comfort, stopping patterns, and ticketing options can differ between services. If a category or vehicle number is missing, treat the entry as incomplete and verify it before relying on it.`;
    travelGuideTips = `For a smoother trip, arrive at the boarding point 15–20 minutes before the selected departure and allow extra time for connecting travel. Ordinary services may use counter or onboard ticketing, while some premium services may offer advance booking through the official Haryana Roadways portal. The official booking and depot information should take precedence over this independent timetable helper.`;
  }

  // Structured schema markup for Search crawler
  // Use static BASE_URL — must be identical on server & client to avoid hydration mismatch
  const webSiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Haryana Roadways Helper",
    "url": "https://haryana-roadways.com",
    "description": "Minimalist WCAG-compliant bus timetable and stops search for Haryana Roadways.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://haryana-roadways.com/?from={from}&to={to}"
      },
      "query-input": "required name=from, required name=to"
    }
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://haryana-roadways.com"
      }
    ]
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Haryana Roadways Helper?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Haryana Roadways Helper is a free, fast, and unofficial search portal to check bus timetables, timing milestones, and routes between various stations in Haryana."
        }
      },
      {
        "@type": "Question",
        "name": "How can I search bus schedules?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Simply choose your boarding station, select your destination, and search. The results will display all matching ordinary and luxury bus departures sorted by time."
        }
      },
      {
        "@type": "Question",
        "name": "Are the bus timings exact?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The timings are indicative based on timetables. Actual timings can vary slightly due to road conditions, and travelers are advised to verify timings with the respective depots."
        }
      }
    ]
  };

  const webPageLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://haryana-roadways.com/#webpage",
    "url": "https://haryana-roadways.com",
    "name": "Haryana Roadways Bus Timetable — Schedules & Route Search",
    "description": "Search Haryana Roadways bus schedules and route stops. Find timings for ordinary and luxury buses between any boarding and destination stations in Haryana.",
    "publisher": {
      "@type": "Organization",
      "name": "Haryana Roadways Helper",
      "logo": {
        "@type": "ImageObject",
        "url": "https://haryana-roadways.com/android-chrome-192x192.png"
      }
    }
  };


  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col text-slate-800 dark:text-slate-100 transition-colors duration-200">
      
      {/* JSON-LD for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(webSiteLd).replace(/</g, "\\u003c"),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbLd).replace(/</g, "\\u003c"),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqLd).replace(/</g, "\\u003c"),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(webPageLd).replace(/</g, "\\u003c"),
        }}
      />

      {/* Premium Top Header */}
      <header className="sticky top-0 z-30 w-full border-b border-slate-200/60 dark:border-slate-900/60 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md transition-colors">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5 logo-bus-hover cursor-pointer" aria-hidden="true">
            {/* Animated SVG Bus Logo */}
            <svg 
              viewBox="0 0 40 28" 
              className="w-8 h-8 text-blue-600 dark:text-blue-500 transition-colors" 
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Bus Shadow */}
              <ellipse cx="20" cy="24" rx="14" ry="2" fill="currentColor" opacity="0.15" />
              
              {/* Dotted Moving Road Line under the bus */}
              <line 
                x1="2" y1="24" x2="38" y2="24" 
                stroke="currentColor" strokeWidth="1.8" 
                strokeLinecap="round" strokeDasharray="3 4" 
                className="logo-road text-slate-300 dark:text-slate-700" 
              />
              
              {/* Bus Body */}
              <g className="logo-bus-body transform origin-bottom transition-transform">
                {/* Main Body Frame */}
                <rect x="6" y="6" width="28" height="15" rx="3" fill="currentColor" />
                {/* Front Windshield Angle Shaving */}
                <path d="M29 6h4.5a1.5 1.5 0 0 1 1.5 1.5v6.5h-6V6Z" fill="currentColor" />
                {/* Windows */}
                <g fill="var(--background)">
                  <rect x="9" y="9" width="5" height="4" rx="0.75" />
                  <rect x="16" y="9" width="5" height="4" rx="0.75" />
                  <rect x="23" y="9" width="5" height="4" rx="0.75" />
                  {/* Front curved windshield */}
                  <path d="M30 9h3.5v4H30a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1Z" />
                </g>
                {/* Headlights (Yellow dot) */}
                <circle cx="33" cy="18" r="1" fill="#eab308" />
                {/* Tail lights (Red dot) */}
                <circle cx="7" cy="18" r="1" fill="#ef4444" />
                {/* Orange/Saffron Brand Accent Stripe */}
                <rect x="6" y="15" width="28" height="1.8" fill="#f97316" />
              </g>
              
              {/* Wheels */}
              <g className="logo-bus-wheel text-slate-800 dark:text-slate-200">
                {/* Back Wheel */}
                <circle cx="12" cy="21" r="3" fill="currentColor" />
                <circle cx="12" cy="21" r="1.2" fill="var(--background)" />
                {/* Front Wheel */}
                <circle cx="28" cy="21" r="3" fill="currentColor" />
                <circle cx="28" cy="21" r="1.2" fill="var(--background)" />
              </g>
            </svg>
            
            {/* Styled Brand Text */}
            <div className="flex flex-col">
              <span className="text-xs font-black uppercase tracking-widest bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                Haryana
              </span>
              <span className="text-[10px] font-bold tracking-tight text-slate-400 uppercase -mt-0.5">
                Roadways
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5">

            <button
              id="theme-toggle-btn"
              onClick={toggleTheme}
              aria-label={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-900 text-slate-500 dark:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-blue-400 outline-none transition-colors"
            >
              {theme === "light" ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m0 13.5V21M4.22 4.22l1.58 1.58m12.42 12.42l1.58 1.58M3 12h2.25m13.5 0H21M6.78 17.22l-1.58 1.58m12.42-12.42l1.58-1.58M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-8">
        
        {/* Breadcrumb (route pages only) — internal linking + SERP context */}
        {currentRoute && slugParts.length === 2 && (
          <nav aria-label="Breadcrumb" className="mb-3">
            <ol className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400 dark:text-slate-500">
              <li>
                <a href="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Home</a>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <a href={`/${slugParts[0]}`} className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors capitalize">
                  {currentRoute.from} Buses
                </a>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-slate-600 dark:text-slate-300" aria-current="page">
                {currentRoute.from} to {currentRoute.to}
              </li>
            </ol>
          </nav>
        )}

        {/* Semantic page context heading */}
        <div className="mb-6 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="space-y-1">
              <h1 className="text-lg font-bold text-slate-900 dark:text-white">
                {fromStations.length > 0 && toStations.length > 0
                  ? `${displayNames(fromStations)} to ${displayNames(toStations)} Bus Timetable`
                  : "Bus Timetable"}
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {fromStations.length > 0 && toStations.length > 0
                  ? `Bus timings, schedule and route stops from ${displayNames(fromStations)} to ${displayNames(toStations)}.`
                  : "Search schedules and route stops for Haryana Roadways state transport."}
              </p>
            </div>
            {totalSearches > 0 && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 animate-fade-in shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                {totalSearches.toLocaleString()} queries served
              </div>
            )}
          </div>
        </div>

        {currentRoute && routeMeta && (
          <div className="mb-6 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 px-3 py-2 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold">
              <span className={routeMeta.isIndexable ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}>
                {routeMeta.isIndexable ? "Verified timetable route" : "Reference route — not currently promoted in search"}
              </span>
              <span aria-hidden="true">·</span>
              <span>{routeMeta.busCount} scheduled service{routeMeta.busCount === 1 ? "" : "s"} recorded</span>
              <span aria-hidden="true">·</span>
              <span>Last checked {routeMeta.lastSeen}</span>
            </div>
            {!routeMeta.isIndexable && (
              <p className="mt-1">
                This page remains available for passengers, but it is excluded from the XML sitemap until the route has a verified, recent service record.
              </p>
            )}
          </div>
        )}

        {/* Minimal Search Card */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200/70 dark:border-slate-800/70 mb-6">
          <form onSubmit={handleSearch} className="space-y-4">
            
            {/* FROM FIELD */}
            <div ref={fromRef} className="relative flex flex-col gap-1.5">
              <label htmlFor="boarding-station-input" className="text-xs font-semibold tracking-wider text-slate-500 dark:text-slate-400 uppercase flex items-center gap-1.5">
                From Boarding Station
                {showKbHint && (
                  <span className="ml-auto font-mono text-[9px] bg-slate-100 dark:bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 transition-opacity">
                    Press /
                  </span>
                )}
              </label>
              
              {/* Added chips */}
              <div className="flex flex-wrap gap-1">
                {fromStations.map((station) => (
                  <span
                    key={station.stationID}
                    className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-md text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/50 dark:border-slate-700/50"
                  >
                    {station.stationName}
                    <button
                      type="button"
                      aria-label={`Remove ${station.stationName} boarding station`}
                      onClick={() => removeFromStation(station.stationID)}
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 font-bold px-0.5 focus-visible:ring-1 focus-visible:ring-blue-600 outline-none"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>

              {/* Input */}
              <div className="relative">
                <input
                  ref={fromInputRef}
                  id="boarding-station-input"
                  type="text"
                  lang="en"
                  inputMode="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="words"
                  enterKeyHint="search"
                  spellCheck={false}
                  value={fromQuery}
                  onChange={(e) => {
                    setFromQuery(e.target.value);
                    setShowFromDropdown(true);
                    if (formError) setFormError("");
                  }}
                  onFocus={() => setShowFromDropdown(true)}
                  placeholder="Search boarding station..."
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showFromDropdown}
                  aria-controls="from-suggestions-list"
                  className="w-full px-3 py-2 text-sm rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-blue-400 outline-none transition-colors"
                />
                {isLoadingFrom && (
                  <span className="absolute inset-y-0 right-3 flex items-center">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                  </span>
                )}
              </div>

              {/* Suggestions listbox */}
              {showFromDropdown && fromSuggestions.length > 0 && (
                <div className="absolute top-[100%] left-0 w-full mt-1 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-800 z-50 max-h-48 overflow-y-auto">
                  {availableFromSuggestions.length === 0 ? (
                    <p className="px-3.5 py-2.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                      Every matching station is already added.
                    </p>
                  ) : (
                    <ul id="from-suggestions-list" role="listbox" aria-label="From station suggestions">
                      {availableFromSuggestions.map((station, index) => (
                        <li key={`${station.stationID}-${index}`} role="option" aria-selected={false}>
                          <button
                            type="button"
                            onClick={() => addFromStation(station)}
                            className="w-full text-left px-3.5 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/60 text-xs font-semibold flex justify-between items-center focus-visible:bg-slate-50 dark:focus-visible:bg-slate-800/60 outline-none"
                          >
                            <span>{station.stationName}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* TO FIELD */}
            <div ref={toRef} className="relative flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="destination-station-input" className="text-xs font-semibold tracking-wider text-slate-500 dark:text-slate-400 uppercase">
                  To Destination Station
                </label>
                {(fromStations.length > 0 || toStations.length > 0) && (
                  <button
                    type="button"
                    onClick={swapStations}
                    aria-label="Swap boarding and destination stations"
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all focus-visible:ring-2 focus-visible:ring-blue-600 cursor-pointer outline-none active:scale-95"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3 h-3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />
                    </svg>
                    Swap
                  </button>
                )}
              </div>

              {/* Added chips */}
              <div className="flex flex-wrap gap-1">
                {toStations.map((station) => (
                  <span
                    key={station.stationID}
                    className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-md text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/50 dark:border-slate-700/50"
                  >
                    {station.stationName}
                    <button
                      type="button"
                      aria-label={`Remove ${station.stationName} destination station`}
                      onClick={() => removeToStation(station.stationID)}
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 font-bold px-0.5 focus-visible:ring-1 focus-visible:ring-blue-600 outline-none"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>

              {/* Input */}
              <div className="relative">
                <input
                  id="destination-station-input"
                  type="text"
                  lang="en"
                  inputMode="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="words"
                  enterKeyHint="search"
                  spellCheck={false}
                  value={toQuery}
                  onChange={(e) => {
                    setToQuery(e.target.value);
                    setShowToDropdown(true);
                    if (formError) setFormError("");
                  }}
                  onFocus={() => setShowToDropdown(true)}
                  placeholder="Search destination station..."
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showToDropdown}
                  aria-controls="to-suggestions-list"
                  className="w-full px-3 py-2 text-sm rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-blue-400 outline-none transition-colors"
                />
                {isLoadingTo && (
                  <span className="absolute inset-y-0 right-3 flex items-center">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                  </span>
                )}
              </div>

              {/* Suggestions listbox */}
              {showToDropdown && toSuggestions.length > 0 && (
                <div className="absolute top-[100%] left-0 w-full mt-1 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-800 z-50 max-h-48 overflow-y-auto">
                  {availableToSuggestions.length === 0 ? (
                    <p className="px-3.5 py-2.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                      Every matching station is already added.
                    </p>
                  ) : (
                    <ul id="to-suggestions-list" role="listbox" aria-label="To station suggestions">
                      {availableToSuggestions.map((station, index) => (
                        <li key={`${station.stationID}-${index}`} role="option" aria-selected={false}>
                          <button
                            type="button"
                            onClick={() => addToStation(station)}
                            className="w-full text-left px-3.5 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/60 text-xs font-semibold flex justify-between items-center focus-visible:bg-slate-50 dark:focus-visible:bg-slate-800/60 outline-none"
                          >
                            <span>{station.stationName}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Action button */}
            <div className="pt-2 flex justify-end gap-2">
              <button
                id="search-timetable-btn"
                type="submit"
                disabled={isResolving || isLoadingTimetable}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-blue-400 outline-none"
              >
                {isResolving && (
                  <span className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" aria-hidden="true"></span>
                )}
                {isResolving ? "Finding station…" : "Search Timetable"}
              </button>
              {(fromStations.length > 0 ||
                toStations.length > 0 ||
                fromQuery ||
                toQuery) && (
                <button
                  id="reset-search-btn"
                  type="button"
                  onClick={resetSearch}
                  className="shrink-0 px-4 py-2.5 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 font-semibold transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-blue-400 outline-none"
                >
                  Reset
                </button>
              )}
            </div>

            {/* Form-level error: free-typed station couldn't be resolved */}
            {formError && (
              <p role="alert" className="text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg px-3 py-2">
                {formError}
              </p>
            )}
          </form>

          {/* FAVORITE BUSES LIST */}
          {favourites.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/50">
              <h2 className="text-[10px] font-bold text-amber-500 uppercase tracking-wider block mb-2">
                ⭐ Saved Buses
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {favourites.map((fav, idx) => (
                  <div
                    key={fav.dutyID ?? idx}
                    className="inline-flex items-center rounded-lg bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => handleRecentSearchClick(fav)}
                      className="pl-2.5 pr-1.5 py-1.5 font-semibold text-left outline-none"
                      aria-label={`Re-search for saved bus ${fav.tripName}`}
                    >
                      <span className="font-black">{formatTime(fav.tripTime)}</span>
                      <span className="mx-1 opacity-60">·</span>
                      <span className="capitalize">{fav.tripName || fav.label}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const next = favourites.filter((f) => f.dutyID !== fav.dutyID);
                        setFavourites(next);
                        localStorage.setItem("favourite_routes", JSON.stringify(next));
                      }}
                      aria-label={`Remove saved bus ${fav.tripName}`}
                      className="pr-2 pl-1 py-1 text-amber-500/60 hover:text-amber-600 dark:hover:text-amber-400 font-bold transition-colors outline-none"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RECENT SEARCHES HISTORY LIST */}
          {recentSearches.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800/50">
              <h2 className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-2">
                Recent Searches
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {recentSearches.map((search, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200/50 dark:border-slate-800/50 text-[11px] text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => handleRecentSearchClick(search)}
                      className="pl-2.5 pr-1.5 py-1 font-semibold text-left outline-none capitalize"
                      aria-label={`Search again from ${displayNames(search.from)} to ${displayNames(search.to)}`}
                    >
                      {displayNames(search.from)} → {displayNames(search.to)}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteRecentSearch(idx)}
                      aria-label={`Remove search from ${displayNames(search.from)} to ${displayNames(search.to)} from history`}
                      className="pr-2 pl-1 py-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 font-bold transition-colors outline-none"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Publisher-authored guidance is visible on the initial homepage, not
            only after a client-side search. This gives first-time visitors
            context about the tool, the data, and its limitations. */}
        {!currentRoute && (
          <section aria-labelledby="about-timetable" className="mb-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-5 space-y-6">
            <div className="space-y-2">
              <h2 id="about-timetable" className="text-base font-bold text-slate-900 dark:text-white">
                Haryana Roadways bus timetable and route guide
              </h2>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                Haryana Roadways Helper is an independent timetable search tool for passengers planning journeys between Haryana Roadways stations. Choose a boarding station and destination to compare scheduled departures, service categories, and available route stops in one place. The service is free to use and does not require an account.
              </p>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                Timetable entries are presented for travel planning and may change when the transport operator changes a schedule, depot, route, or vehicle assignment. We are not affiliated with the official Haryana Roadways transport department. For a time-sensitive journey, allow a buffer and verify the departure with the relevant bus stand or official transport source.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <article className="rounded-xl bg-slate-50 dark:bg-slate-950/60 p-4">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">How to use the search</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                  Search for the boarding and destination stations, select the matching suggestions, and submit the form. Results are sorted from morning to evening so you can compare departures quickly.
                </p>
              </article>
              <article className="rounded-xl bg-slate-50 dark:bg-slate-950/60 p-4">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">What the results show</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                  Each available service can include a departure time, trip name, depot, vehicle category, vehicle number, and stop milestones. Some fields may be unavailable when the upstream timetable does not provide them.
                </p>
              </article>
              <article className="rounded-xl bg-slate-50 dark:bg-slate-950/60 p-4">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Planning responsibly</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                  Check the date and direction before travelling, arrive early, and confirm important or late-night journeys with the bus stand. This site should supplement, not replace, official travel information.
                </p>
              </article>
            </div>

            <div className="space-y-4">
              <h2 className="text-base font-bold text-slate-900 dark:text-white">Frequently asked questions</h2>
              <div className="space-y-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">What is Haryana Roadways Helper?</h3>
                  <p className="mt-1">Haryana Roadways Helper is a free, fast, and unofficial search portal to check bus timetables, timing milestones, and routes between various stations in Haryana.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">How can I search bus schedules?</h3>
                  <p className="mt-1">Simply choose your boarding station, select your destination, and search. The results will display all matching ordinary and luxury bus departures sorted by time.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Are the bus timings exact?</h3>
                  <p className="mt-1">The timings are indicative based on timetables. Actual timings can vary slightly due to road conditions, and travelers are advised to verify timings with the respective depots.</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* RESULTS AREA */}
        {hasSearched && (
          <div className="space-y-4">
            
            {/* Header info / popular badge */}
            {!isLoadingTimetable && !searchError && timetable.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                  Available Services
                </h2>
                {mounted && getRouteCount(fromStations, toStations) >= 3 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                    🔥 Popular Route
                  </span>
                )}
              </div>
            )}

            {/* Feature 1: countdown banner */}
            {nextBus && countdownLabel && !isLoadingTimetable && !searchError && (
              <div
                id="next-bus-banner-card"
                tabIndex={0}
                role="button"
                aria-haspopup="dialog"
                aria-label={`Next upcoming bus departs at ${formatTime(nextBus.tripTime)}, ${nextBus.tripName || `${nextBus.boardingStationName} to ${nextBus.destinationStationName}`}. Starting in ${countdownLabel}. Press enter to view route stops.`}
                onClick={() => handleBusClick(nextBus, "next-bus-banner-card")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleBusClick(nextBus, "next-bus-banner-card");
                  }
                }}
                className="sticky top-[66px] z-20 bg-gradient-to-r from-blue-600/95 to-indigo-600/95 dark:from-blue-700/95 dark:to-indigo-700/95 backdrop-blur-md text-white rounded-xl p-4 shadow-lg border border-blue-500/20 dark:border-blue-500/10 flex items-center justify-between animate-card-load transition-all cursor-pointer hover:brightness-105 active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-blue-600 dark:focus-visible:ring-blue-400 outline-none"
              >
                <div className="space-y-0.5">
                  <span className="text-[10px] uppercase font-extrabold tracking-wider opacity-90">
                    Next Upcoming Bus
                  </span>
                  <h3 className="text-sm font-bold">
                    Departs at {formatTime(nextBus.tripTime)}
                  </h3>
                  <p className="text-[10px] opacity-80 font-medium line-clamp-1">
                    {nextBus.tripName || `${nextBus.boardingStationName} → ${nextBus.destinationStationName}`}
                  </p>
                </div>
                <div className="text-right flex flex-col items-end gap-0.5 shrink-0">
                  <span className="text-[9px] font-bold opacity-85 uppercase tracking-wider">Starting in</span>
                  <span className="text-sm font-black tracking-tight bg-white/20 px-2.5 py-0.5 rounded-lg border border-white/25 animate-pulse">
                    {countdownLabel}
                  </span>
                </div>
              </div>
            )}

            {/* Feature 7: all departure indicator */}
            {allPast && !isLoadingTimetable && !searchError && (
              <div className="bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-400 rounded-xl p-3.5 flex items-center gap-3 animate-card-load">
                <span className="text-lg" role="img" aria-label="Clock">⏰</span>
                <div className="space-y-0.5">
                  <h3 className="text-xs font-bold">
                    All buses for today have departed
                  </h3>
                  <p className="text-[10px] text-amber-700/80 dark:text-amber-400/80 font-medium">
                    Displaying full scheduled service timetable below.
                  </p>
                </div>
              </div>
            )}

            {/* Feature 4: vehicle type filters */}
            {!isLoadingTimetable && !searchError && timetable.length > 0 && vehicleTypes.length > 0 && (
              <div className="flex items-center gap-1.5 overflow-x-auto py-1 scrollbar-none">
                <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mr-1 select-none shrink-0">
                  Filter:
                </span>
                <button
                  type="button"
                  onClick={() => { haptic(5); setActiveFilter("All"); }}
                  className={`px-3 py-1 text-[10px] font-bold rounded-full border transition-all shrink-0 ${
                    activeFilter === "All"
                      ? "bg-blue-600 border-blue-600 text-white shadow-xs"
                      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700"
                  }`}
                >
                  All ({timetable.length})
                </button>
                {vehicleTypes.map((type) => {
                  const count = timetable.filter((b) => b.vehicleType1 === type).length;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => { haptic(5); setActiveFilter(type); analytics.capture("vehicle_filter_applied", { filter: type }); }}
                      className={`px-3 py-1 text-[10px] font-bold rounded-full border transition-all shrink-0 ${
                        activeFilter === type
                          ? "bg-blue-600 border-blue-600 text-white shadow-xs"
                          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700"
                      }`}
                    >
                      {type} ({count})
                    </button>
                  );
                })}
              </div>
            )}

            {isLoadingTimetable ? (
              // Feature 6: Shimmering cards replacing layout shifts
              <SkeletonCards />
            ) : searchError ? (
              <div className="p-3 text-center text-xs font-medium text-rose-500 bg-rose-50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/50 rounded-xl" role="alert">
                {searchError}
              </div>
            ) : timetable.length === 0 ? (
              // Feature 3: Sleeping bus animated empty state
              <SleepingBus />
            ) : filteredTimetable.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <span className="text-xl">🔍</span>
                <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">No services match the &quot;{activeFilter}&quot; filter.</p>
                <button
                  type="button"
                  onClick={() => setActiveFilter("All")}
                  className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline outline-none"
                >
                  Reset Filter
                </button>
              </div>
            ) : (
              <div className="space-y-2" role="region" aria-label="Search Results">
                {filteredTimetable.map((bus, idx) => {
                  const elementId = `bus-card-${bus.dutyID}-${idx}`;
                  return (
                    <div
                      key={bus.dutyID + "-" + idx}
                      id={elementId}
                      tabIndex={0}
                      onClick={() => handleBusClick(bus, elementId)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleBusClick(bus, elementId);
                        }
                      }}
                      className="p-4 bg-white dark:bg-slate-900 border border-slate-200/50 dark:border-slate-900 rounded-xl hover:border-blue-600 dark:hover:border-blue-500/50 transition-colors cursor-pointer flex justify-between items-center focus-visible:ring-2 focus-visible:ring-blue-600 outline-none animate-card-load opacity-0"
                      style={{ animationDelay: `${idx * 40}ms` }}
                      role="button"
                      aria-haspopup="dialog"
                      aria-label={`Bus departure time ${formatTime(bus.tripTime)} from ${bus.boardingStationName} to ${bus.destinationStationName}, depot name ${bus.depotName}, service type ${bus.vehicleType1}. Press enter to view route stops.`}
                    >
                      <div className="flex items-center gap-2">
                        {/* Star / Favorite toggle — per individual bus trip */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavourite(bus);
                          }}
                          aria-label={isFavourite(bus) ? "Remove from favorites" : "Add to favorites"}
                          className={`p-1.5 rounded-lg transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-600 active:scale-90 ${
                            isFavourite(bus)
                              ? "text-amber-500 hover:text-amber-600 dark:hover:text-amber-400"
                              : "text-slate-300 hover:text-slate-400 dark:text-slate-700 dark:hover:text-slate-600"
                          }`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={isFavourite(bus) ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2} className="w-4 h-4" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499c.158-.343.644-.343.802 0l2.715 5.517 6.084.884c.378.055.529.518.256.784l-4.4 4.288 1.04 6.059c.065.378-.332.667-.67.488L12 18.213l-5.438 2.859c-.338.179-.73-.11-.67-.488l1.04-6.059-4.4-4.288c-.273-.266-.123-.729.256-.784l6.084-.884 2.715-5.517Z" />
                          </svg>
                        </button>

                        <div className="space-y-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm font-bold tracking-tight text-blue-600 dark:text-blue-400">
                              {formatTime(bus.tripTime)}
                            </span>
                            <span className="text-[10px] text-slate-400 font-semibold tracking-wider uppercase">
                              {bus.depotName} Depot
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 font-semibold">
                            {bus.tripName || `${bus.boardingStationName} → ${bus.destinationStationName}`}
                          </p>
                        </div>
                      </div>

                      <div className="text-right space-y-1">
                        <span className="text-[10px] px-2 py-0.5 font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded border border-slate-200/40 dark:border-slate-700/40">
                          {bus.vehicleType1 || "Ordinary"}
                        </span>
                        <p className="text-[10px] text-slate-400 font-mono">
                          {bus.vehicleNumber ? bus.vehicleNumber.trim() : "HR-XX-XXXX"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Dynamic Route Travel Guide (400-500 words) */}
        {currentRoute && timetable.length > 0 && (
          <section aria-label="Route Guide and Information" className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-800/60 space-y-4 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-white">
              {currentRoute.from} to {currentRoute.to} Haryana Roadways Bus Guide & Timings
            </h2>
            
            <p>
              {travelGuideIntro}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-2">
              <div className="bg-slate-100/60 dark:bg-slate-900/50 rounded-lg p-3 border border-slate-200/50 dark:border-slate-800/40">
                <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-1 text-xs">Boarding Location & Terminal</h3>
                <p className="text-[11px] leading-relaxed">
                  Buses typically depart from the main Haryana Roadways Inter-State Bus Terminus (ISBT) or local bus stands in <strong>{currentRoute.from}</strong>. We advise checking the timetable details above to identify exact boarding points.
                </p>
              </div>
              <div className="bg-slate-100/60 dark:bg-slate-900/50 rounded-lg p-3 border border-slate-200/50 dark:border-slate-800/40">
                <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-1 text-xs">Destination Terminus</h3>
                <p className="text-[11px] leading-relaxed">
                  Upon arrival in <strong>{currentRoute.to}</strong>, buses terminate at the central bus stand or designated depot hubs, allowing easy access to local auto-rickshaws, metro networks, and regional connecting buses.
                </p>
              </div>
            </div>

            <h3 className="font-bold text-slate-800 dark:text-slate-200">Timetable Summary & Operational Depots</h3>
            <p>
              {travelGuideStats}
            </p>

            <h3 className="font-bold text-slate-800 dark:text-slate-200">Haryana Roadways Fleet & Comfort Levels</h3>
            <p>
              {travelGuideTypes}
            </p>

            <h3 className="font-bold text-slate-800 dark:text-slate-200">Ticket Booking & Reservation Guidance</h3>
            <p>
              {travelGuideTips}
            </p>

            <h3 className="font-bold text-slate-800 dark:text-slate-200">Travel Advice & Best Practices</h3>
            <p>
              Since timings are subject to seasonal factors, traffic congestion, and administrative adjustments, it is recommended to reach the boarding bay 15–20 minutes before the scheduled departure of your selected bus stand run. You can expand specific bus entries in the available services grid above to verify intermediate milestones and timing checkpoints. Always keep a buffer time when traveling to catch connecting services.
            </p>
          </section>
        )}

        {/* SEO internal links: related routes on a route page, otherwise the
            popular-routes grid on the homepage. Server-rendered for crawlers. */}
        <section aria-label="Explore more bus routes" className="mt-10 pt-8 border-t border-slate-100 dark:border-slate-800/50 space-y-6">
          {currentRoute ? (
            (() => {
              const related = relatedRoutes(currentRoute.from, currentRoute.to);
              return (
                <>
                  <RouteLinks
                    title={`Popular routes from ${currentRoute.from}`}
                    routes={[related.reverse, ...related.fromOrigin]}
                  />
                  <RouteLinks
                    title={`Buses to ${currentRoute.to}`}
                    routes={related.toDestination}
                  />
                </>
              );
            })()
          ) : (
            <RouteLinks title="Popular bus routes" routes={popularRouteLinks()} />
          )}
        </section>
      </main>

      {/* Feature 5: PWA Install slide-up prompt */}
      {showPwaPrompt && (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-xl z-50 animate-slide-up flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <span className="text-2xl" role="img" aria-label="Bus App Icon">🚌</span>
            <div className="space-y-0.5">
              <div className="text-xs font-bold text-slate-900 dark:text-white">Haryana Roadways Helper</div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Add to your home screen for quick offline timetable access.</p>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={handlePwaDismiss}
              className="px-2.5 py-1 text-[10px] font-bold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors outline-none"
            >
              Later
            </button>
            <button
              type="button"
              onClick={handlePwaInstall}
              className="px-3.5 py-1.5 text-[10px] font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors outline-none active:scale-95 cursor-pointer"
            >
              Install
            </button>
          </div>
        </div>
      )}

      {/* STOPS DETAILS MODAL */}
      {isStopsOpen && selectedBus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="modal-route-title">
          <div
            onClick={closeStopsModal}
            className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-xs"
          />

          <div className="relative w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xl max-h-[75vh] flex flex-col">
            
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between">
              <div>
                <h3 id="modal-route-title" className="text-sm font-bold text-slate-900 dark:text-white">
                  {selectedBus.tripName || "Route Stops"}
                </h3>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {selectedBus.vehicleNumber ? selectedBus.vehicleNumber.trim() : "HR-XX-XXXX"} • {selectedBus.vehicleType1}
                </p>
              </div>
              <button
                onClick={closeStopsModal}
                aria-label="Close route stops details"
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xs font-semibold focus-visible:ring-1 focus-visible:ring-blue-600 outline-none p-1"
              >
                ✕
              </button>
            </div>

            {/* Modal Scroll Content */}
            <div className="flex-1 overflow-y-auto p-5 scroll-smooth">
              {isLoadingStops ? (
                // Entertaining custom bus loading graphics
                <LoadingBus />
              ) : busStops.length === 0 ? (
                <p className="text-center text-xs text-slate-400">No stop coordinates loaded.</p>
              ) : (
                <div className="relative pl-4 border-l border-slate-200 dark:border-slate-800 ml-2 space-y-4">
                  {busStops.map((stop, index) => {
                    const isStart = index === 0;
                    const isEnd = index === busStops.length - 1;
                    return (
                      <div key={stop.srNo || index} className="relative animate-card-load opacity-0" style={{ animationDelay: `${index * 50}ms` }}>
                        {/* Indian design theme node indicators: Start (Orange/Saffron), Middle (Royal Blue), End (Green) */}
                        <span className={`absolute -left-[21px] top-1 flex h-2.5 w-2.5 items-center justify-center rounded-full ${
                          isStart 
                            ? "bg-orange-600" 
                            : isEnd 
                            ? "bg-emerald-600" 
                            : "bg-blue-600"
                        }`} aria-hidden="true" />
                        <div className="text-xs">
                          <h4 className="font-semibold text-slate-800 dark:text-slate-200">
                            {stop.boardingStationName}
                          </h4>
                          <span className="text-[10px] text-slate-400">
                            {formatTime(stop.tripTime)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between gap-2">
              {/* Share route stops button */}
              <button
                type="button"
                onClick={shareRouteStops}
                disabled={isLoadingStops || busStops.length === 0}
                aria-label="Share this bus route schedule"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors focus-visible:ring-2 focus-visible:ring-blue-600 outline-none cursor-pointer active:scale-95"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                </svg>
                Share Route
              </button>

              <button
                ref={modalCloseBtnRef}
                onClick={closeStopsModal}
                className="px-3.5 py-1.5 rounded bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-semibold text-xs transition-colors focus-visible:ring-2 focus-visible:ring-blue-600 outline-none"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Footer: navigation, disclaimers, developer credit */}
      <SiteFooter />

      {/* Share FAB — appears when a timetable is loaded */}
      {hasSearched && fromStations.length > 0 && toStations.length > 0 && (
        <div className="fixed bottom-6 right-5 z-40 flex flex-col items-end gap-2">
          {/* Tooltip label */}
          {shareCopied && (
            <span className="bg-emerald-600 text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg shadow-lg animate-card-load">
              Link copied! ✓
            </span>
          )}
          <button
            id="share-fab-btn"
            type="button"
            onClick={handleShare}
            aria-label="Share this bus timetable"
            className="group w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-90 text-white shadow-lg hover:shadow-blue-500/40 dark:hover:shadow-blue-600/30 flex items-center justify-center transition-all duration-200 focus-visible:ring-4 focus-visible:ring-blue-400 focus-visible:ring-offset-2 outline-none cursor-pointer"
          >
            {shareCopied ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 group-hover:scale-110 transition-transform" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
              </svg>
            )}
          </button>
        </div>
      )}

    </div>
  );
}
